<template>
  <base-card no-header>
    <template #body>
      <el-row class="items-stretch broker-row">
        <el-col :span="6">
          <h3>{{ configuration.brokerId }}</h3>
          <h3 class="body-1 cursor-pointer mt-1" @click="openVersionModal">
            {{ $t('currentVersion') + ': ' + configuration.version }}
          </h3>
          <el-tooltip placement="top" :content="$t('disableBrokerSftpNote')">
            <el-switch
              v-model="form.enabled"
              :active-text="$t('enabledCapitalized')"
              :inactive-text="$t('disabledCapitalized')"
              @change="handleChangeEnabled"
            />
          </el-tooltip>
        </el-col>
        <el-col :span="15">
          <el-form
            ref="formRef"
            :model="form"
            :rules="formRules"
            label-position="top"
          >
            <el-divider>{{ $t('connectionDetails') }}</el-divider>
            <el-form-item class="mb-0" :label="$t('hostName')" prop="hostName">
              <el-input
                v-model="form.hostName"
                :validate-event="false"
                size="small"
                @change="handleChangeHostName"
              />
            </el-form-item>
            <el-form-item class="mb-0" :label="$t('userName')" prop="userName">
              <el-input
                v-model="form.userName"
                :validate-event="false"
                size="small"
                @change="handleChangeUserName"
              />
            </el-form-item>
            <el-form-item
              class="mb-0"
              :label="$t('passwordKeyName')"
              :validate-event="false"
              prop="passwordKeyName"
            >
              <el-tooltip
                placement="top"
                :content="$t('brokerSftpPasswordSshKeysTooltipMessage')"
              >
                <el-input
                  v-model="form.passwordKeyName"
                  :validate-event="false"
                  size="small"
                  @change="handleChangePasswordKeyName"
                />
              </el-tooltip>
            </el-form-item>
            <el-form-item
              class="mb-0"
              :label="$t('sshKeyKeyName')"
              prop="sshKeyKeyName"
            >
              <el-tooltip
                placement="top"
                :content="$t('brokerSftpPasswordSshKeysTooltipMessage')"
              >
                <el-input
                  v-model="form.sshKeyKeyName"
                  :validate-event="false"
                  size="small"
                  @change="handleChangeSshKeyKeyName"
                />
              </el-tooltip>
            </el-form-item>
            <el-form-item class="mb-0" :label="$t('port')" prop="port">
              <el-input-number
                v-model="form.port"
                size="small"
                :min="1"
                :max="65535"
                :precision="0"
                :step="1"
                :value-on-clear="null"
                @change="handleChangePort"
              />
            </el-form-item>
            <el-divider>{{ $t('fileFetchingSettings') }}</el-divider>
            <el-form-item class="mb-0" :label="$t('scanPath')" prop="scanPath">
              <el-tooltip
                placement="top"
                :content="$t('scanPathOptionalTooltipContent')"
              >
                <el-input
                  v-model="form.scanPath"
                  :validate-event="false"
                  size="small"
                  @change="handleChangeScanPath"
                />
              </el-tooltip>
            </el-form-item>
            <el-tooltip
              placement="top"
              :content="$t('fileFiltersTooltipContent')"
            >
              <el-divider>
                {{ $t('fileFilters') }}
              </el-divider>
            </el-tooltip>
            <template v-if="!form.fileFilters.length">
              <el-button
                type="primary"
                size="small"
                class="mt-2 cursor-pointer"
                @click="addFilter"
              >
                + {{ $t('add') }}
              </el-button>
            </template>

            <template v-else>
              <el-form-item
                v-for="(ff, idx) in form.fileFilters"
                :key="`ff-${idx}`"
                class="mb-2"
                :prop="`fileFilters.${idx}.namePattern`"
              >
                <el-row :gutter="10" class="filter-row">
                  <el-col :span="22">
                    <el-input
                      v-model="ff.namePattern"
                      size="small"
                      clearable
                      @change="handleFilterChange(idx, ff.namePattern)"
                    />
                  </el-col>
                  <el-col :span="2">
                    <el-button
                      type="default"
                      size="small"
                      @click="removeFilter(idx)"
                    >
                      {{ $t('remove') }}
                    </el-button>
                  </el-col>
                </el-row>
              </el-form-item>

              <el-button type="primary" size="small" @click="addFilter">
                + {{ $t('add') }}
              </el-button>
            </template>
          </el-form>
        </el-col>
        <el-col :span="3" class="actions-col-wrapper">
          <div class="actions-col">
            <img
              :src="require(`@/assets/images/icons/new/delete.svg`)"
              height="20"
              width="20"
              class="action-icon cursor-pointer"
              @click="deleteItem"
            />
            <el-button type="primary" size="large" @click="saveItem">
              {{ $t('save') }}
            </el-button>
          </div>
        </el-col>
      </el-row>
    </template>
  </base-card>
  <gain-configuration-versions-modal
    v-model="showVersionModal"
    :configuration-versions="props.configurationHistory"
    @restore="handleRestore"
    @close="showVersionModal = false"
  />
</template>
<script lang="ts">
import BaseCard from '@/components/panels/BaseCard.vue';
import GainConfigurationVersionsModal from './GainConfigurationVersionsModal.vue';
import { onMounted, onUpdated, reactive, ref } from 'vue';
import {
  ElButton,
  ElCol,
  ElDivider,
  ElForm,
  ElFormItem,
  ElInput,
  ElInputNumber,
  ElRow,
  ElSwitch,
  ElTooltip
} from 'element-plus';
import {
  BrokerSftpConfiguration,
  FileFilter
} from '@etp/etp-gain-bff-client/axios';
import i18n from '@/i18n';
import { requireAtLeastOne } from '@/utils/formValidators';

export default {
  name: 'BrokerSftpConfigurationCard',
  components: {
    BaseCard,
    ElButton,
    ElCol,
    ElDivider,
    ElForm,
    ElFormItem,
    ElInput,
    ElInputNumber,
    ElRow,
    ElSwitch,
    ElTooltip,
    GainConfigurationVersionsModal
  },
  props: {
    configuration: {
      type: Object as () => BrokerSftpConfiguration,
      required: true
    },
    configurationHistory: {
      type: Array as () => Array<BrokerSftpConfiguration>,
      default: () => new Array<BrokerSftpConfiguration>()
    }
  },
  emits: ['delete', 'update', 'save', 'fetch-history'],
  setup(props, { emit }) {
    const showVersionModal = ref(false);

    const openVersionModal = () => {
      emit('fetch-history');
      showVersionModal.value = true;
    };

    const handleRestore = (input: BrokerSftpConfiguration) => {
      emit('update', input);
      showVersionModal.value = false;
    };

    interface FormType {
      enabled: boolean;
      hostName: string;
      userName: string;
      passwordKeyName: string;
      sshKeyKeyName: string;
      port: number;
      scanPath: string;
      fileFilters: FileFilter[];
    }

    const form = ref<FormType>({
      enabled: true,
      hostName: '',
      userName: '',
      passwordKeyName: '',
      sshKeyKeyName: '',
      port: 0,
      scanPath: '',
      fileFilters: [] as FileFilter[]
    });
    const formRef = ref<HTMLFormElement>();
    const formRules = reactive({
      hostName: [
        {
          required: true,
          message: i18n.global.t('thisFieldIsRequired')
        }
      ],
      userName: [
        {
          required: true,
          message: i18n.global.t('thisFieldIsRequired')
        }
      ],
      passwordKeyName: [
        {
          validator: requireAtLeastOne<FormType>(
            ['passwordKeyName', 'sshKeyKeyName'],
            () => form.value
          )
        }
      ],
      sshKeyKeyName: [
        {
          validator: requireAtLeastOne<FormType>(
            ['passwordKeyName', 'sshKeyKeyName'],
            () => form.value
          )
        }
      ],
      port: [
        {
          required: true,
          message: i18n.global.t('thisFieldIsRequired')
        }
      ]
    });

    const emitUpdateEvent = async () => {
      emit('update', {
        enabled: form.value.enabled,
        hostName: form.value.hostName,
        userName: form.value.userName,
        passwordKeyName: form.value.passwordKeyName,
        sshKeyKeyName: form.value.sshKeyKeyName,
        port: form.value.port,
        scanPath: form.value.scanPath,
        fileFilters: form.value.fileFilters
      } as BrokerSftpConfiguration);
    };

    const saveItem = async () => {
      await formRef.value?.validate((valid: boolean) => {
        if (valid) {
          emit('save');
        }
      });
    };
    const deleteItem = async () => emit('delete');

    const handleChangeEnabled = async (newValue: string | number | boolean) => {
      form.value.enabled = Boolean(newValue);
      await emitUpdateEvent();
    };

    const handleChangeHostName = async (newValue: string) => {
      form.value.hostName = newValue;
      await emitUpdateEvent();
    };

    const handleChangeUserName = async (newValue: string) => {
      form.value.userName = newValue;
      await emitUpdateEvent();
    };

    const handleChangePasswordKeyName = async (newValue: string) => {
      form.value.passwordKeyName = newValue;
      await emitUpdateEvent();
    };

    const handleChangeSshKeyKeyName = async (newValue: string) => {
      form.value.sshKeyKeyName = newValue;
      await emitUpdateEvent();
    };

    const handleChangePort = async (newValue: number | undefined) => {
      form.value.port = newValue ?? 0;
      await emitUpdateEvent();
    };

    const handleChangeScanPath = async (newValue: string) => {
      form.value.scanPath = newValue;
      await emitUpdateEvent();
    };

    const addFilter = async () => {
      form.value.fileFilters.push({ namePattern: '' });
      await emitUpdateEvent();
    };

    const removeFilter = async (index: number) => {
      form.value.fileFilters.splice(index, 1);
      await emitUpdateEvent();
    };

    const handleFilterChange = async (
      index: number,
      val: string | null | undefined
    ) => {
      form.value.fileFilters[index].namePattern = (val ?? '').toString();
      await emitUpdateEvent();
    };

    const updateLocalVars = async () => {
      form.value.enabled = props.configuration.enabled ?? true;
      form.value.hostName = props.configuration.hostName ?? '';
      form.value.userName = props.configuration.userName ?? '';
      form.value.passwordKeyName = props.configuration.passwordKeyName ?? '';
      form.value.sshKeyKeyName = props.configuration.sshKeyKeyName ?? '';
      form.value.port = props.configuration.port ?? 0;
      form.value.scanPath = props.configuration.scanPath ?? '';
      form.value.fileFilters = props.configuration.fileFilters ?? [];
    };

    onMounted(async () => {
      await updateLocalVars();
      await emitUpdateEvent();
    });
    onUpdated(async () => await updateLocalVars());

    return {
      deleteItem,
      saveItem,
      form,
      formRef,
      formRules,
      handleChangeEnabled,
      handleChangeHostName,
      handleChangeUserName,
      handleChangePasswordKeyName,
      handleChangeSshKeyKeyName,
      handleChangePort,
      handleChangeScanPath,
      addFilter,
      removeFilter,
      handleFilterChange,
      openVersionModal,
      showVersionModal,
      handleRestore,
      props
    };
  }
};
</script>
<style lang="scss" scoped>
.broker-row {
  align-items: stretch;
}

.actions-col-wrapper {
  align-self: stretch;
  display: flex;
}

.actions-col {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  min-height: 100%;
}

:deep(.el-form-item.mb-0.is-error) {
  margin-bottom: 18px !important;
}

.filter-row {
  width: 100%;
}
</style>
